import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-CO", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export const createReservationSkill: Skill = {
  id: "createReservation",
  description:
    "Crea una reserva de mesa. Necesita tableId, clientId y scheduledAt (ISO). Si solo conoces el nombre del cliente, primero usa createClient o pídele al usuario que provea el ID. Si no conoces tableId, sugiérele al usuario abrir la pestaña 'mesas' o 'reservas' para verlas.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      tableId: { type: "string", description: "ID de la mesa a reservar" },
      clientId: { type: "string", description: "ID del cliente registrado" },
      scheduledAt: {
        type: "string",
        description:
          "Fecha y hora ISO 8601 (ej. 2026-04-30T19:00:00). Acepta solo fecha + hora.",
      },
      partySize: {
        type: "number",
        description: "Número de comensales (opcional)",
      },
      notes: { type: "string", description: "Notas (opcional)" },
    },
    required: ["restaurantId", "tableId", "clientId", "scheduledAt"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) {
      return { ok: false, error: "Sesión no disponible." };
    }
    const restaurantId = String(args.restaurantId ?? "");
    const tableId = String(args.tableId ?? "");
    const clientId = String(args.clientId ?? "");
    const scheduledAt = String(args.scheduledAt ?? "");
    if (!restaurantId || !tableId || !clientId || !scheduledAt) {
      return {
        ok: false,
        error: "Faltan datos: restaurantId, tableId, clientId, scheduledAt.",
      };
    }

    let scheduledDate: Date;
    try {
      scheduledDate = new Date(scheduledAt);
      if (Number.isNaN(scheduledDate.getTime())) throw new Error("invalid");
    } catch {
      return {
        ok: false,
        error: `Fecha inválida: ${scheduledAt}. Usa formato ISO YYYY-MM-DDTHH:mm.`,
      };
    }
    const scheduledIso = scheduledDate.toISOString();

    const partySize = args.partySize ? Number(args.partySize) : undefined;
    const notes = args.notes ? String(args.notes) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Crear reserva",
      description: "Vas a registrar una nueva reserva de mesa.",
      details: [
        { label: "Cuándo", value: formatDateTime(scheduledIso) },
        ...(partySize
          ? [{ label: "Comensales", value: String(partySize) }]
          : []),
        { label: "Mesa", value: tableId },
        { label: "Cliente", value: clientId },
      ],
      confirmLabel: "Reservar",
    });
    if (!ok) {
      return { ok: false, error: "Acción cancelada por el usuario." };
    }

    if (ctx.follow.enabled) {
      await ctx.follow.step("Creando reserva…", { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="reservas"]`, {
        durationMs: 700,
        label: "Reservas",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=reservas`,
      );
      await sleep(900);
      await ctx.follow.step("Guardando…", { durationMs: 500 });
    }

    try {
      const body: Record<string, unknown> = {
        tableId,
        clientId,
        scheduledAt: scheduledIso,
      };
      if (partySize) body.partySize = partySize;
      if (notes) body.notes = notes;

      const { data } = await apiWithAuth.post(
        `/restaurants/${restaurantId}/reservations`,
        body,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Reserva creada", {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "reservations",
        action: "created",
        restaurantId,
        id: data?.id,
      });

      return {
        ok: true,
        result: {
          id: data?.id,
          scheduledAt: data?.scheduledAt ?? scheduledIso,
          tableId,
          clientId,
        },
        humanSummary: `Reserva creada para ${formatDateTime(scheduledIso)}.`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al crear reserva", {
          durationMs: 700,
          spinner: false,
        });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error:
          err?.response?.data?.message ||
          err?.message ||
          "Error creando reserva.",
      };
    }
  },
};
